// src/views/Admin/Locales/LocalPage/components/CreateUserModal/CreateUserModal.tsx

import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { 
  HiOutlineUserPlus, 
  HiOutlineXMark,
  HiOutlineEye,
  HiOutlineEyeSlash,
  HiOutlineChevronRight,
  HiOutlineChevronLeft,
  HiOutlineUser,
  HiOutlineBriefcase,
  HiOutlineEnvelope,
  HiOutlinePhone,
  HiOutlineCalendar,
  HiOutlineMapPin,
  HiOutlineLockClosed
} from 'react-icons/hi2';
import { toast } from 'react-toastify';
import { createUser } from '../../../../../../crud/users_api/users';
import { getAllRoles } from '../../../../../../crud/configuration_api/roles';
import './CreateUserModal.scss';

interface Role {
  rol_id: number;
  rol_name: string;
}

interface Puesto {
  puesto_id: number;
  puesto_name: string;
}

interface CreateUserModalProps {
  isOpen: boolean;
  localId: number;
  localName: string;
  puestos: Puesto[];
  onClose: () => void;
  onSuccess: () => void;
  onCreatePuesto: () => void;
}

interface FormData {
  username: string;
  last_name: string;
  middle_name: string;
  email: string;
  password: string;
  phone_number: string;
  address: string;
  dob: string;
  rol_id: number | null;
  puesto_id: number | null;
}

const initialFormData: FormData = {
  username: '',
  last_name: '',
  middle_name: '',
  email: '',
  password: '',
  phone_number: '',
  address: '',
  dob: '',
  rol_id: null,
  puesto_id: null
};

const CreateUserModal: React.FC<CreateUserModalProps> = ({
  isOpen,
  localId,
  localName,
  puestos,
  onClose,
  onSuccess,
  onCreatePuesto
}) => {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loadingRoles, setLoadingRoles] = useState(false);

  // Load roles when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData(initialFormData);
      setCurrentStep(1);
      setError(null);
      setShowPassword(false);
      loadRoles();
    }
  }, [isOpen]);

  const loadRoles = async () => {
    setLoadingRoles(true);
    try {
      const response = await getAllRoles();
      setRoles(response.data || []);
    } catch (err) {
      console.error('Error loading roles:', err);
      toast.error('Error al cargar roles');
    } finally {
      setLoadingRoles(false);
    }
  };

  const handleChange = (field: keyof FormData, value: string | number | null) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const validateStep1 = (): boolean => {
    if (!formData.username.trim()) {
      setError('El nombre es requerido');
      return false;
    }
    if (!formData.last_name.trim()) {
      setError('El apellido es requerido');
      return false;
    }
    if (!formData.email.trim()) {
      setError('El email es requerido');
      return false;
    }
    if (!formData.email.includes('@') || !formData.email.includes('.')) {
      setError('El email no tiene un formato válido');
      return false;
    }
    if (!formData.password) {
      setError('La contraseña es requerida');
      return false;
    }
    if (formData.password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return false;
    }
    return true;
  };

  const validateStep2 = (): boolean => {
    if (!formData.rol_id) {
      setError('Debe seleccionar un rol');
      return false;
    }
    return true;
  };

  const handleNextStep = () => {
    if (validateStep1()) {
      setError(null);
      setCurrentStep(2);
    }
  };

  const handlePrevStep = () => {
    setError(null);
    setCurrentStep(1);
  };

  const handleSubmit = async () => {
    if (!validateStep2()) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const userData = {
        username: formData.username.trim(),
        last_name: formData.last_name.trim(),
        middle_name: formData.middle_name?.trim() || null,
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        phone_number: formData.phone_number?.trim() || '',
        address: formData.address?.trim() || '',
        dob: formData.dob || null,
        local_id: localId,
        rol_id: formData.rol_id,
        puesto_id: formData.puesto_id || null,
        state: true
      };

      await createUser(userData);
      toast.success('Usuario creado exitosamente');
      onSuccess();
      onClose();
    } catch (err: any) {
      const message = err?.response?.data?.message || 'Error al crear el usuario';
      setError(typeof message === 'string' ? message : 'Error al crear el usuario');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  return (
    <Modal
      show={isOpen}
      onHide={handleClose}
      centered
      backdrop={isSubmitting ? 'static' : true}
      keyboard={!isSubmitting}
      dialogClassName="portal-create-user-modal"
    >
      <div className="portal-create-user-modal-content">
        <button className="close-button" onClick={handleClose} disabled={isSubmitting}>
          <HiOutlineXMark size={20} />
        </button>

        <div className="modal-header">
          <div className="modal-icon">
            <HiOutlineUserPlus size={28} />
          </div>
          <h2>Crear Usuario</h2>
          <p className="modal-subtitle">
            Para <strong>{localName}</strong>
          </p>

          {/* Step indicator */}
          <div className="step-indicator">
            <div className={`step ${currentStep === 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}>
              <span className="step-number">1</span>
              <span className="step-label">Información Personal</span>
            </div>
            <div className="step-divider"></div>
            <div className={`step ${currentStep === 2 ? 'active' : ''}`}>
              <span className="step-number">2</span>
              <span className="step-label">Rol y Puesto</span>
            </div>
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="modal-body">
          {/* Step 1: Personal Information */}
          {currentStep === 1 && (
            <div className="form-step">
              <div className="form-row">
                <div className="form-group">
                  <label>
                    <HiOutlineUser size={14} />
                    Nombre *
                  </label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => handleChange('username', e.target.value)}
                    placeholder="Juan"
                    disabled={isSubmitting}
                  />
                </div>
                <div className="form-group">
                  <label>
                    <HiOutlineUser size={14} />
                    Apellido *
                  </label>
                  <input
                    type="text"
                    value={formData.last_name}
                    onChange={(e) => handleChange('last_name', e.target.value)}
                    placeholder="Pérez"
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>
                  <HiOutlineEnvelope size={14} />
                  Email *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="juan.perez@email.com"
                  disabled={isSubmitting}
                />
              </div>

              <div className="form-group">
                <label>
                  <HiOutlineLockClosed size={14} />
                  Contraseña *
                </label>
                <div className="password-input">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => handleChange('password', e.target.value)}
                    placeholder="Mínimo 6 caracteres"
                    disabled={isSubmitting}
                  />
                  <button 
                    type="button"
                    className="toggle-password"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    {showPassword ? <HiOutlineEyeSlash size={18} /> : <HiOutlineEye size={18} />}
                  </button>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>
                    <HiOutlinePhone size={14} />
                    Teléfono
                  </label>
                  <input
                    type="tel"
                    value={formData.phone_number}
                    onChange={(e) => handleChange('phone_number', e.target.value)}
                    placeholder="+56 9 1234 5678"
                    disabled={isSubmitting}
                  />
                </div>
                <div className="form-group">
                  <label>
                    <HiOutlineCalendar size={14} />
                    Fecha de Nacimiento
                  </label>
                  <input
                    type="date"
                    value={formData.dob}
                    onChange={(e) => handleChange('dob', e.target.value)}
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>
                  <HiOutlineMapPin size={14} />
                  Dirección
                </label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  placeholder="Av. Principal 123"
                  disabled={isSubmitting}
                />
              </div>
            </div>
          )}

          {/* Step 2: Role and Puesto */}
          {currentStep === 2 && (
            <div className="form-step">
              <div className="form-group">
                <label>
                  <HiOutlineUser size={14} />
                  Rol *
                </label>
                {loadingRoles ? (
                  <div className="loading-select">Cargando roles...</div>
                ) : (
                  <div className="select-options">
                    {roles.map(role => (
                      <button
                        key={role.rol_id}
                        type="button"
                        className={`option-btn ${formData.rol_id === role.rol_id ? 'selected' : ''}`}
                        onClick={() => handleChange('rol_id', role.rol_id)}
                        disabled={isSubmitting}
                      >
                        {role.rol_name}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>
                  <HiOutlineBriefcase size={14} />
                  Puesto de Trabajo
                </label>
                {puestos.length === 0 ? (
                  <div className="no-puestos-warning">
                    <p>No hay puestos de trabajo creados para este local.</p>
                    <button 
                      type="button" 
                      className="create-puesto-link"
                      onClick={() => {
                        onClose();
                        onCreatePuesto();
                      }}
                    >
                      <HiOutlineBriefcase size={16} />
                      Crear un puesto primero
                    </button>
                  </div>
                ) : (
                  <div className="select-options">
                    <button
                      type="button"
                      className={`option-btn ${formData.puesto_id === null ? 'selected' : ''}`}
                      onClick={() => handleChange('puesto_id', null)}
                      disabled={isSubmitting}
                    >
                      Sin puesto
                    </button>
                    {puestos.map(puesto => (
                      <button
                        key={puesto.puesto_id}
                        type="button"
                        className={`option-btn ${formData.puesto_id === puesto.puesto_id ? 'selected' : ''}`}
                        onClick={() => handleChange('puesto_id', puesto.puesto_id)}
                        disabled={isSubmitting}
                      >
                        {puesto.puesto_name}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="user-summary">
                <h4>Resumen del usuario</h4>
                <div className="summary-item">
                  <span className="summary-label">Nombre:</span>
                  <span className="summary-value">{formData.username} {formData.last_name}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Email:</span>
                  <span className="summary-value">{formData.email}</span>
                </div>
                {formData.rol_id && (
                  <div className="summary-item">
                    <span className="summary-label">Rol:</span>
                    <span className="summary-value">
                      {roles.find(r => r.rol_id === formData.rol_id)?.rol_name || '-'}
                    </span>
                  </div>
                )}
                {formData.puesto_id && (
                  <div className="summary-item">
                    <span className="summary-label">Puesto:</span>
                    <span className="summary-value">
                      {puestos.find(p => p.puesto_id === formData.puesto_id)?.puesto_name || '-'}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="modal-actions">
          {currentStep === 1 ? (
            <>
              <button 
                className="cancel-button" 
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancelar
              </button>
              <button 
                className="next-button"
                onClick={handleNextStep}
                disabled={isSubmitting}
              >
                Siguiente
                <HiOutlineChevronRight size={18} />
              </button>
            </>
          ) : (
            <>
              <button 
                className="back-button" 
                onClick={handlePrevStep}
                disabled={isSubmitting}
              >
                <HiOutlineChevronLeft size={18} />
                Atrás
              </button>
              <button 
                className="submit-button"
                onClick={handleSubmit}
                disabled={isSubmitting || !formData.rol_id}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner" />
                    Creando...
                  </>
                ) : (
                  <>
                    <HiOutlineUserPlus size={18} />
                    Crear Usuario
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default CreateUserModal;
